import qs from "qs"
import { LoaderCircleIcon, SaveIcon, TriangleAlertIcon, XCircleIcon } from "lucide-react"
import { Fragment, useCallback, useEffect, useEffectEvent, useId, useMemo, useRef, useState } from "react"
import { Outlet, useLoaderData, useRouteLoaderData, useBeforeUnload, useBlocker } from "react-router"

import { Input } from "@/components/ui/input"
import { Button, buttonVariants } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

import { hasPermission } from "@/machines/Authmachine"
import { debounce, fetchAPI, handleResponse } from "@/utils"
import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"
import { useConfirmableFetcher } from "@/hooks/useConfirmableFetcher"

import type { RootLoader } from "@/routes/root"

type RetentionBrandConfig = UnityViewer.Retention.BrandConfig

const routeId = "retention"
const sessionKey = "retention-filter-storage"

const CUSTOM_DURATION_SENTINEL = "_custom_"

const DURATION_OPTIONS = [
  { label: "1 Year", value: "P1Y" },
  { label: "2 Years", value: "P2Y" },
  { label: "3 Years", value: "P3Y" },
] as const

const PRESET_DURATION_VALUES = DURATION_OPTIONS.map((o) => o.value)

const DEFAULT_CONFIG: RetentionBrandConfig = {
  defaultRetentionDuration: "",
  maxExtensionDuration: null,
  retentionDurationChangesRequireReason: false,
  automaticallyDeleteExpiredCases: false,
  deletedCaseRecoveryDuration: null,
  authorizedUsersMayReduceRetentionDuration: false,
  authorizedUsersMayReduceBelowBrandMinimum: false,
  authorizedUsersMayDeleteBeforeExpiry: false,
  twoUserDeletionRequired: false,
  casesMayBeSelectedForIndefiniteRetention: false,
}

interface AffectedCountResponse {
  affectedCaseCount: number
  immediatelyExpiredCount: number
}

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string[] }
  | { status: "success"; data: AffectedCountResponse }

interface ConfirmationTokenResponse {
  requiresConfirmation: boolean
  token: string
  expires: string
  reasons: string[]
  affectedCaseCount?: number
  immediatelyExpiredCount?: number
}

interface ActionResult {
  action: "SAVE" | "DELETE" | "UNKNOWN"
  errorMessages?: string[]
  successMessage?: string
  confirmationData?: ConfirmationTokenResponse
}

type DurationParts = { years: string; months: string; days: string; hours: string; minutes: string }

const ISO_REGEX = /^P(?:0D|0S|(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?)$/
function parseIsoDuration(iso: string): DurationParts {
  const [, years = "", months = "", days = "", hours = "", minutes = ""] = ISO_REGEX.exec(iso) ?? []
  return { years, months, days, hours, minutes }
}

function buildIsoDuration(
  years: string | number,
  months: string | number,
  days: string | number,
  hours: string | number,
  minutes: string | number,
) {
  const y = Number(years)
  const m = Number(months)
  const d = Number(days)
  const h = Number(hours)
  const mi = Number(minutes)
  if (y === 0 && m === 0 && d === 0 && h === 0 && mi === 0) return null

  let out = "P"
  if (y) out += `${y}Y`
  if (m) out += `${m}M`
  if (d) out += `${d}D`
  if (h || mi) {
    out += "T"
    if (h) out += `${h}H`
    if (mi) out += `${mi}M`
  }
  return out
}

function formatDurationLabel(iso: string | null): string {
  if (!iso) return "Not Set"
  const preset = DURATION_OPTIONS.find((o) => o.value === iso)
  if (preset) return preset.label

  const { years, months, days, hours, minutes } = parseIsoDuration(iso)

  const parts: string[] = []
  if (years) parts.push(`${years} year${years === "1" ? "" : "s"}`)
  if (months) parts.push(`${months} month${months === "1" ? "" : "s"}`)
  if (days) parts.push(`${days} day${days === "1" ? "" : "s"}`)
  if (hours) parts.push(`${hours} hour${hours === "1" ? "" : "s"}`)
  if (minutes) parts.push(`${minutes} minute${minutes === "1" ? "" : "s"}`)

  return parts.length > 0 ? parts.join(", ") : iso
}

function isoToFields(iso: string | null): DurationParts {
  return iso ? parseIsoDuration(iso) : { years: "", months: "", days: "", hours: "", minutes: "" }
}

const durationPartKey = (prefix: string, field: string) => `${prefix}-${field}`

const CURRENT_DURATION_INPUT_ID = "current-retention-duration"

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]
  const projects = getProjectsByOrganisation(loadedOrganisation)
  const loadedProject =
    filters.parsed?.project == null ? projects[0] : (projects.find((p) => p === filters.parsed?.project) ?? projects[0])

  let configExists = true
  let configLoadFailed = false
  const config = await fetchAPI<RetentionBrandConfig>(
    `/api/UnityViewer/Retention/${loadedOrganisation}/${loadedProject}`,
  )
    .then((res) => {
      if (res.status === 404) {
        configExists = false
        return DEFAULT_CONFIG
      }
      return handleResponse(res)
    })
    .catch((err): RetentionBrandConfig => {
      console.error("[Retention] Failed to load config:", err)
      configLoadFailed = true
      return DEFAULT_CONFIG
    })

  return { config, configExists, configLoadFailed, loadedOrganisation, loadedProject, projects }
}

async function action({ request }: { request: Request }): Promise<ActionResult> {
  const formData = await request.formData()
  const { intent, project, organisation, defaultRetentionDuration, token } = Object.fromEntries(formData) as Record<
    string,
    string
  >

  if (intent === "DELETE") {
    try {
      const query = token ? `?token=${encodeURIComponent(token)}` : ""
      const res = await fetchAPI(`/api/UnityViewer/Retention/${organisation}/${project}${query}`, {
        method: "DELETE",
      })

      if (res.status === 409) {
        const data = (await res.json()) as ConfirmationTokenResponse
        return { action: "DELETE", confirmationData: data }
      }

      if (res.status === 406) {
        let messages = [
          "Your confirmation token is invalid or has expired. Please re-submit the removal to generate a new confirmation.",
        ]
        try {
          const errData = (await res.json()) as { reason?: string }
          if (errData?.reason) messages = [errData.reason]
        } catch (err) {
          console.error("[Retention] Failed to parse 406 response:", err)
        }
        return { action: "DELETE", errorMessages: messages }
      }

      if (res.status === 403) {
        let messages = ["You do not have permission to remove this retention configuration."]
        try {
          const errData = (await res.json()) as { message?: string }
          if (errData?.message) messages = [errData.message]
        } catch (err) {
          console.error("[Retention] Failed to parse 403 response:", err)
        }
        return { action: "DELETE", errorMessages: messages }
      }

      if (!res.ok) {
        let messages: string[] = []
        try {
          const errData = (await res.json()) as { detail?: string; title?: string }
          if (errData?.detail) messages = [errData.detail]
          else if (errData?.title) messages = [errData.title]
        } catch (err) {
          console.error("[Retention] Failed to parse error response:", err)
        }
        return {
          action: "DELETE",
          errorMessages:
            messages.length === 0 ? ["Failed to remove retention configuration. Please try again."] : messages,
        }
      }

      return {
        action: "DELETE",
        successMessage: "Retention configuration removed. This brand no longer has a retention policy set.",
      }
    } catch (err) {
      console.error("[Retention] Delete failed:", err)
      return {
        action: "DELETE",
        errorMessages: [err instanceof Error ? err.message : "A network error occurred. Please try again."],
      }
    }
  }

  if (intent !== "SAVE") return { action: "UNKNOWN", errorMessages: ["Unknown action"] }

  const getBool = (key: string) => formData.get(key) === "on"
  const getDurationISO = (prefix: string) =>
    buildIsoDuration(
      (formData.get(durationPartKey(prefix, "years")) as string) ?? "",
      (formData.get(durationPartKey(prefix, "months")) as string) ?? "",
      (formData.get(durationPartKey(prefix, "days")) as string) ?? "",
      (formData.get(durationPartKey(prefix, "hours")) as string) ?? "",
      (formData.get(durationPartKey(prefix, "minutes")) as string) ?? "",
    )

  try {
    const maxExtensionDuration = getDurationISO("maxExtension")
    if (maxExtensionDuration != null && !ISO_REGEX.test(maxExtensionDuration))
      return { action: "SAVE", errorMessages: ["Invalid max extension duration format."] }

    const deletedCaseRecoveryDuration = getDurationISO("recovery")
    if (deletedCaseRecoveryDuration != null && !ISO_REGEX.test(deletedCaseRecoveryDuration))
      return { action: "SAVE", errorMessages: ["Invalid deleted case recovery duration format."] }

    const res = await fetchAPI(`/api/UnityViewer/Retention/${organisation}/${project}`, {
      method: "PUT",
      body: {
        defaultRetentionDuration,
        maxExtensionDuration,
        retentionDurationChangesRequireReason: getBool("retention-changes-require-reason"),
        automaticallyDeleteExpiredCases: getBool("auto-delete"),
        deletedCaseRecoveryDuration,
        authorizedUsersMayReduceRetentionDuration: getBool("may-reduce-duration"),
        authorizedUsersMayReduceBelowBrandMinimum: getBool("may-reduce-below-minimum"),
        authorizedUsersMayDeleteBeforeExpiry: getBool("may-delete-before-expiry"),
        twoUserDeletionRequired: getBool("two-user-deletion"),
        casesMayBeSelectedForIndefiniteRetention: getBool("indefinite-retention"),
        // in case of confirmation
        token,
      } satisfies RetentionBrandConfig & { token?: string },
    })

    if (res.status === 409) {
      const data = (await res.json()) as ConfirmationTokenResponse
      return { action: "SAVE", confirmationData: data }
    }
    if (res.status === 406) {
      let messages = [
        "Your confirmation token is invalid or has expired. Please re-submit the change to generate a new confirmation.",
      ]
      try {
        const errData = (await res.json()) as { reason?: string }
        if (errData?.reason) messages = [errData.reason]
      } catch (err) {
        console.error("[Retention] Failed to parse 406 response:", err)
      }
      return { action: "SAVE", errorMessages: messages }
    }

    if (!res.ok) {
      let messages: string[] = []
      try {
        const errData = await res.json()
        if (Array.isArray(errData) && errData.length > 0) {
          const collected = (errData as Array<{ propertyName?: string; errorMessage?: string }>)
            .filter((e) => Boolean(e?.errorMessage))
            .map((e) => e.errorMessage!)
          if (collected.length > 0) messages = collected
        } else if (typeof errData === "object" && errData !== null && "message" in errData) {
          messages = [(errData as { message: string }).message]
        }
      } catch (err) {
        console.error("[Retention] Failed to parse error response:", err)
      }

      return {
        action: "SAVE",
        errorMessages: messages.length === 0 ? ["Failed to save retention config. Please try again."] : messages,
      }
    }

    return {
      action: "SAVE",
      successMessage:
        "Brand retention duration updated successfully. Changes will apply to affected cases within 30 minutes.",
    }
  } catch (err) {
    console.error("[Retention] Save failed:", err)
    return {
      action: "SAVE",
      errorMessages: [err instanceof Error ? err.message : "A network error occurred. Please try again."],
    }
  }
}

interface DurationPartsProps {
  parts: DurationParts
  namePrefix?: string
  requiresAtLeastOnePart?: boolean
  onPartChange?: (part: keyof DurationParts, value: string) => void
  groupLabel?: string
  describedBy?: string
}

function DurationParts({
  parts,
  namePrefix,
  requiresAtLeastOnePart = false,
  onPartChange,
  groupLabel,
  describedBy,
}: DurationPartsProps) {
  const hintId = `${namePrefix ?? "retention"}-parts-hint`
  const ariaDescribedBy = [describedBy, requiresAtLeastOnePart ? hintId : undefined].filter(Boolean).join(" ")
  return (
    <div className="mt-2 grid grid-cols-2 gap-3 px-0.5 pb-0.5 sm:grid-cols-5">
      {(["years", "months", "days", "hours", "minutes"] as const).map((field) => {
        const inputId = namePrefix ? `${namePrefix}-${field}` : field
        return (
          <div key={field} className="space-y-1">
            <label htmlFor={inputId} className="text-xs font-medium text-muted-foreground capitalize">
              {field}
            </label>
            <Input
              id={inputId}
              type="number"
              min={requiresAtLeastOnePart ? 1 : 0}
              placeholder="0"
              aria-label={groupLabel ? `${groupLabel} ${field}` : undefined}
              aria-describedby={ariaDescribedBy || undefined}
              className="h-9 focus-visible:ring-0 focus-visible:ring-offset-0"
              name={inputId}
              defaultValue={parts[field]}
              required={requiresAtLeastOnePart}
              onChange={(e) => {
                const val = e.target.value
                if (val === "" || parseInt(val) >= 0) onPartChange?.(field, val)
              }}
            />
          </div>
        )
      })}
      {requiresAtLeastOnePart && (
        <p
          id={hintId}
          className="col-span-2 flex items-start gap-1.5 text-xs font-medium text-gray-600 sm:col-span-5 dark:text-gray-300"
        >
          At least one of the above duration parts must be set.
        </p>
      )}
    </div>
  )
}
interface DurationSectionProps {
  label: string
  description?: string
  fields: DurationParts
  namePrefix: string
}

function DurationSection({ label, description, fields, namePrefix }: DurationSectionProps) {
  const descriptionId = description ? `${namePrefix}-description` : undefined
  return (
    <fieldset className="space-y-1" aria-describedby={descriptionId}>
      <legend className="text-sm font-medium">{label}</legend>
      {description && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      <DurationParts parts={fields} namePrefix={namePrefix} groupLabel={label} describedBy={descriptionId} />
    </fieldset>
  )
}

interface CheckboxRowProps {
  id: string
  label: string
  description: string
  defaultChecked: boolean
}

function CheckboxRow({ id, label, description, defaultChecked }: CheckboxRowProps) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-4">
      <input
        type="checkbox"
        id={id}
        name={id}
        defaultChecked={defaultChecked}
        className="mt-0.5 rounded border border-input"
        aria-describedby={`${id}-description`}
      />
      <div>
        <label htmlFor={id} className="text-sm leading-none font-medium">
          {label}
        </label>
        <p id={`${id}-description`} className="mt-1 text-xs text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  )
}

type ConfirmableFetcher = ReturnType<typeof useConfirmableFetcher<ActionResult>>

interface ConfirmationDialogProps {
  confirmationData: ConfirmationTokenResponse | null | undefined
  timeLeft: number
  isSaving: boolean
  onConfirm: () => void
  onCancel: () => void
  title?: string
  description?: string
  confirmLabel?: string
  confirmingLabel?: string
}

function ConfirmationDialog({
  confirmationData,
  timeLeft,
  isSaving,
  onConfirm,
  onCancel,
  title = "Confirm dangerous change",
  description = "This action requires confirmation before it can be applied. Review the details below.",
  confirmLabel = "Confirm & apply",
  confirmingLabel = "Applying…",
}: ConfirmationDialogProps) {
  const timerSeconds = Math.floor(timeLeft % 60)
  const timerMinutes = Math.floor(timeLeft / 60)
  const isExpired = timeLeft === 0

  const showAffectedCount = confirmationData?.affectedCaseCount != null
  const showImmediatelyExpired =
    confirmationData?.immediatelyExpiredCount != null && confirmationData.immediatelyExpiredCount > 0
  const timerRef = useRef<HTMLDivElement>(null)
  const timerTextId = useId()
  const timerText = isExpired
    ? "Confirmation expired"
    : `Confirmation valid for ${timerMinutes} ${timerMinutes === 1 ? "minute" : "minutes"} ${timerSeconds} ${timerSeconds === 1 ? "second" : "seconds"}`
  const [timerAnnouncement, setTimerAnnouncement] = useState("")
  useEffect(() => {
    if (confirmationData == null) {
      setTimerAnnouncement("")
      return
    }
    const rounded = Math.ceil(timeLeft)
    if (rounded > 60) setTimerAnnouncement("")
    else if (rounded === 60) setTimerAnnouncement("Less than one minute remaining to confirm.")
    else if (rounded === 0) setTimerAnnouncement("Confirmation expired.")
  }, [timeLeft, confirmationData])

  return (
    <Dialog
      open={confirmationData != null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent
        className="max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          // Land on the countdown so screen readers announce the remaining time first.
          if (timerRef.current == null) return
          e.preventDefault()
          timerRef.current.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <TriangleAlertIcon className="size-5" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {confirmationData && (
            <>
              <ul className="space-y-1">
                {confirmationData.reasons.map((reason, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    {reason}
                  </li>
                ))}
              </ul>
              {(showAffectedCount || showImmediatelyExpired) && (
                <div
                  className={`grid gap-3 ${showAffectedCount && showImmediatelyExpired ? "grid-cols-2" : "grid-cols-1"}`}
                >
                  {showAffectedCount && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-center dark:border-amber-700 dark:bg-amber-950">
                      <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">
                        {confirmationData.affectedCaseCount!.toLocaleString()}
                      </p>
                      <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">cases affected</p>
                    </div>
                  )}
                  {showImmediatelyExpired && (
                    <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-center dark:border-red-700 dark:bg-red-950">
                      <p className="text-2xl font-bold text-red-800 dark:text-red-200">
                        {confirmationData.immediatelyExpiredCount!.toLocaleString()}
                      </p>
                      <p className="mt-0.5 text-xs text-red-700 dark:text-red-300">deleted immediately</p>
                    </div>
                  )}
                </div>
              )}
              <div
                ref={timerRef}
                tabIndex={0}
                className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 ${
                  timeLeft > 60
                    ? "border-border bg-muted/30"
                    : "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
                }`}
                role="timer"
                aria-labelledby={timerTextId}
              >
                <span id={timerTextId} className="sr-only">
                  {timerText}
                </span>
                <span className="text-muted-foreground" aria-hidden>
                  Confirmation valid for
                </span>
                <span className="font-mono font-medium" aria-hidden>
                  {isExpired ? (
                    <span className="text-destructive">Expired</span>
                  ) : (
                    `${timerMinutes}:${String(timerSeconds).padStart(2, "0")}`
                  )}
                </span>
              </div>
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                {timerAnnouncement}
              </div>
              {isExpired && (
                <p className="text-sm text-destructive" role="alert">
                  Confirmation expired. Please re-submit the change to generate a new confirmation token.
                </p>
              )}
            </>
          )}
          <div className="flex items-center justify-end gap-3 border-t pt-3">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              aria-describedby={confirmationData ? timerTextId : undefined}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={timeLeft === 0 || isSaving}
              onClick={onConfirm}
              aria-label={confirmLabel}
              aria-describedby={confirmationData ? timerTextId : undefined}
            >
              {isSaving ? (
                <>
                  <LoaderCircleIcon className="size-4 animate-spin" />
                  {confirmingLabel}
                </>
              ) : (
                confirmLabel
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function Retention() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((formData, reason) => {
    if (reason.type === "field" && reason.name === "organisation") formData.delete("project")
  }, [])

  return (
    <Fragment>
      <div>
        <h1 className="text-lg font-semibold">Retention</h1>
        <p className="mt-1 text-sm text-muted-foreground">Configure how long case data is retained.</p>
      </div>
      {rootData != null && data != null && (rootData.organisations.length > 1 || data.projects.length > 1) && (
        <div className="flex items-center gap-4">
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data.loadedOrganisation}
              items={rootData.organisations}
            />
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
          </FiltersWrapper>
        </div>
      )}
      <Outlet key={data != null ? `${data.loadedOrganisation}-${data.loadedProject}` : undefined} />
    </Fragment>
  )
}

function Main() {
  const { config, configExists, configLoadFailed, loadedProject, loadedOrganisation } = useLoaderData<typeof loader>()

  const configKey = useMemo(() => JSON.stringify(config), [config])

  const save = useConfirmableFetcher<ActionResult>()
  const del = useConfirmableFetcher<ActionResult>()

  const hasPendingConfirmation = save.hasPendingToken || del.hasPendingToken

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (!hasPendingConfirmation) return
        e.preventDefault()
        e.returnValue = "Your confirmation is pending. If you navigate away, you will need to re-submit the change."
      },
      [hasPendingConfirmation],
    ),
  )

  const blocker = useBlocker(hasPendingConfirmation)

  const blockerEvent = useEffectEvent(() => {
    const confirmed = window.confirm(
      "Your confirmation is pending. If you navigate away, you will need to re-submit the change.",
    )

    if (confirmed) blocker.proceed!()
    else blocker.reset!()
  })

  useEffect(() => {
    if (blocker.state !== "blocked") return
    blockerEvent()
  }, [blocker.state])

  return (
    <>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {save.successMessage ?? ""}
      </div>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {del.successMessage ?? ""}
      </div>
      <RetentionForm
        key={configKey}
        config={config}
        configExists={configExists}
        configLoadFailed={configLoadFailed}
        loadedProject={loadedProject}
        loadedOrganisation={loadedOrganisation}
        save={save}
        del={del}
      />
    </>
  )
}

interface RetentionFormProps {
  config: RetentionBrandConfig
  configExists: boolean
  configLoadFailed: boolean
  loadedProject: string
  loadedOrganisation: string
  save: ConfirmableFetcher
  del: ConfirmableFetcher
}

function RetentionForm({
  config,
  configExists,
  configLoadFailed,
  loadedProject,
  loadedOrganisation,
  save,
  del,
}: RetentionFormProps) {
  const RETENTION_DURATION_ID = "retention-duration-type"

  const currentDuration = config.defaultRetentionDuration ?? ""
  const [durationSelection, setDurationSelection] = useState(() => {
    if (!currentDuration) return

    if (PRESET_DURATION_VALUES.includes(currentDuration as (typeof PRESET_DURATION_VALUES)[number])) {
      return { kind: "preset" as const, value: currentDuration as (typeof PRESET_DURATION_VALUES)[number] }
    }

    return { kind: "custom" as const, value: parseIsoDuration(currentDuration) }
  })
  const selectedDuration = useMemo(() => {
    if (durationSelection == null) return ""
    if (durationSelection.kind === "preset") return durationSelection.value
    return (
      buildIsoDuration(
        durationSelection.value.years,
        durationSelection.value.months,
        durationSelection.value.days,
        durationSelection.value.hours,
        durationSelection.value.minutes,
      ) ?? ""
    )
  }, [durationSelection])
  const previewState = useFetchPreview(loadedOrganisation, loadedProject, selectedDuration, currentDuration)

  const [formTouched, setFormTouched] = useState(false)

  useEffect(() => {
    if (save.successMessage != null) setFormTouched(false)
  }, [save.successMessage])

  const hasChanges = formTouched && save.successMessage == null

  const canWrite = hasPermission("writeAccess")
  const canDelete = hasPermission("deleteAccess")
  const isDisabled = save.isSubmitting || del.isSubmitting || !canWrite
  const isRemoveDisabled = del.isSubmitting || save.isSubmitting || !canDelete || !configExists || configLoadFailed

  const handleCustomFieldChange = (field: keyof DurationParts, value: string) =>
    setDurationSelection((cur) =>
      cur?.kind === "custom" ? { kind: "custom", value: { ...cur.value, [field]: value } } : cur,
    )
  const handleDropdownChange = (val: string) =>
    setDurationSelection(
      val === CUSTOM_DURATION_SENTINEL
        ? { kind: "custom", value: { years: "", months: "", days: "", hours: "", minutes: "" } }
        : { kind: "preset", value: val as (typeof PRESET_DURATION_VALUES)[number] },
    )

  return (
    <>
      <save.fetcher.Form
        ref={save.formRef}
        method="PUT"
        className="mx-auto flex min-h-0 max-w-5xl grow flex-col gap-(--gap) py-6 [--gap:--spacing(6)] *:min-h-0"
        onChange={(e: React.ChangeEvent<HTMLFormElement>) =>
          setFormTouched(
            [...e.currentTarget.elements].some((_el) => {
              const el = _el as HTMLInputElement

              if (el.id === RETENTION_DURATION_ID) return selectedDuration !== currentDuration
              if (el.tagName !== "INPUT") return false

              if (el.type === "checkbox") return el.checked !== el.defaultChecked
              return el.value !== el.defaultValue
            }),
          )
        }
      >
        <div
          role="group"
          aria-labelledby={CURRENT_DURATION_INPUT_ID}
          className="shrink-0 rounded-lg border bg-muted/30 px-4 py-3"
        >
          <span
            id={CURRENT_DURATION_INPUT_ID}
            className="block text-xs font-medium tracking-wide text-muted-foreground uppercase"
          >
            Current duration
          </span>

          <p className="mt-1 px-1 py-0.5 text-sm font-medium">{formatDurationLabel(config.defaultRetentionDuration)}</p>
        </div>
        <fieldset disabled={isDisabled} className="space-y-(--gap) overflow-y-auto">
          {configLoadFailed && (
            <div
              className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
              role="alert"
            >
              <p className="flex items-center gap-1.5 font-medium">
                <TriangleAlertIcon className="size-4 shrink-0" aria-hidden />
                Couldn&apos;t load the current retention configuration. The form below shows defaults, not the saved
                values — reload the page before making changes.
              </p>
            </div>
          )}
          <div className="relative space-y-2 overflow-visible">
            <label htmlFor={RETENTION_DURATION_ID} className="text-sm font-medium">
              Retention duration<span className="text-destructive">*</span>
            </label>
            <Select
              required={selectedDuration === ""}
              value={
                durationSelection == null
                  ? undefined
                  : durationSelection.kind === "preset"
                    ? durationSelection.value
                    : CUSTOM_DURATION_SENTINEL
              }
              onValueChange={handleDropdownChange}
            >
              <SelectTrigger
                id={RETENTION_DURATION_ID}
                className="w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label="Select retention duration"
              >
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CUSTOM_DURATION_SENTINEL}>Custom</SelectItem>
                {DURATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {durationSelection?.kind === "custom" && (
              <>
                <DurationParts
                  parts={durationSelection.value}
                  onPartChange={handleCustomFieldChange}
                  groupLabel="Retention duration"
                  requiresAtLeastOnePart={
                    (durationSelection.value.years === "" || +durationSelection.value.years < 1) &&
                    (durationSelection.value.months === "" || +durationSelection.value.months < 1) &&
                    (durationSelection.value.days === "" || +durationSelection.value.days < 1) &&
                    (durationSelection.value.hours === "" || +durationSelection.value.hours < 1) &&
                    (durationSelection.value.minutes === "" || +durationSelection.value.minutes < 1)
                  }
                />
                {+durationSelection.value.years < 1 &&
                  +durationSelection.value.months < 1 &&
                  +durationSelection.value.days < 1 &&
                  (+durationSelection.value.hours > 0 || +durationSelection.value.minutes > 0) && (
                    <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                      <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                      Setting retention duration in hours or minutes alone may result in cases expiring very quickly.
                    </p>
                  )}
              </>
            )}
            <input type="hidden" name="defaultRetentionDuration" value={selectedDuration} />
            <input type="hidden" name="intent" value="SAVE" />
            <input type="hidden" name="organisation" value={loadedOrganisation} />
            <input type="hidden" name="project" value={loadedProject} />
            {save.confirmationData != null && <input type="hidden" name="token" value={save.confirmationData.token} />}
            {previewState.status === "loading" && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <LoaderCircleIcon className="size-3.5 animate-spin" />
                Calculating affected cases…
              </p>
            )}
            {previewState.status === "error" && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                role="alert"
              >
                {previewState.message.length === 1 ? (
                  <p className="flex items-center gap-1.5">
                    <TriangleAlertIcon className="size-4 shrink-0" aria-hidden />
                    {previewState.message[0]}
                  </p>
                ) : (
                  <ul className="mt-1 list-disc space-y-1 pl-6">
                    {previewState.message.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {previewState.status === "success" && (
              <div
                role="status"
                aria-live="polite"
                className={`rounded-md border px-3 py-2 text-sm ${
                  previewState.data.immediatelyExpiredCount > 0
                    ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
                    : "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200"
                }`}
              >
                <p className="flex items-center gap-1.5 font-medium">
                  {previewState.data.affectedCaseCount.toLocaleString()} cases expiry dates will be affected by this
                  change.
                </p>
                {previewState.data.immediatelyExpiredCount > 0 && (
                  <p className="mt-1">
                    {previewState.data.immediatelyExpiredCount.toLocaleString()} cases will be marked for immediate
                    deletion (recovery period begins).
                  </p>
                )}
              </div>
            )}
          </div>
          <DurationSection
            label="Max extension duration"
            description="Limits how much a user can extend a retention duration. Leave empty for no limit."
            fields={isoToFields(config.maxExtensionDuration)}
            namePrefix="maxExtension"
          />
          <DurationSection
            label="Deleted case recovery duration"
            description="How long deleted cases can be recovered before permanent deletion. Leave empty for no recovery period."
            fields={isoToFields(config.deletedCaseRecoveryDuration)}
            namePrefix="recovery"
          />
          <CheckboxRow
            id="auto-delete"
            label="Automatically delete expired cases"
            description="When enabled, cases that have passed their retention expiry will be permanently deleted during the next scheduled run."
            defaultChecked={config.automaticallyDeleteExpiredCases}
          />
          <CheckboxRow
            id="retention-changes-require-reason"
            label="Retention duration changes require reason"
            description="When enabled, users must provide a reason when changing the retention duration."
            defaultChecked={config.retentionDurationChangesRequireReason}
          />
          <CheckboxRow
            id="may-reduce-duration"
            label="Authorised users may reduce retention duration"
            description="When enabled, authorised users are permitted to reduce the retention duration."
            defaultChecked={config.authorizedUsersMayReduceRetentionDuration}
          />
          <CheckboxRow
            id="may-reduce-below-minimum"
            label="Authorised users may reduce below brand minimum"
            description="When enabled, authorised users can reduce retention below the brand minimum."
            defaultChecked={config.authorizedUsersMayReduceBelowBrandMinimum}
          />
          <CheckboxRow
            id="may-delete-before-expiry"
            label="Authorised users may delete before expiry"
            description="When enabled, authorised users can delete cases before they expire."
            defaultChecked={config.authorizedUsersMayDeleteBeforeExpiry}
          />
          <CheckboxRow
            id="two-user-deletion"
            label="Two user deletion required"
            description="When enabled, deletion of cases requires approval from two users."
            defaultChecked={config.twoUserDeletionRequired}
          />
          <CheckboxRow
            id="indefinite-retention"
            label="Cases may be selected for indefinite retention"
            description="When enabled, cases can be marked to be retained indefinitely."
            defaultChecked={config.casesMayBeSelectedForIndefiniteRetention}
          />
        </fieldset>
        <section className="flex shrink-0 items-end justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            {save.successMessage != null && save.successMessage !== "" && (
              <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                {save.successMessage}
              </div>
            )}
            {save.errorMessages != null && save.errorMessages.length > 0 && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                role="alert"
              >
                {save.errorMessages.length === 1 ? (
                  save.errorMessages[0]
                ) : (
                  <ul className="list-disc space-y-1 pl-4">
                    {save.errorMessages.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {del.successMessage != null && del.successMessage !== "" && (
              <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
                {del.successMessage}
              </div>
            )}
            {del.errorMessages != null && del.errorMessages.length > 0 && (
              <div
                className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                role="alert"
              >
                {del.errorMessages.length === 1 ? (
                  del.errorMessages[0]
                ) : (
                  <ul className="list-disc space-y-1 pl-4">
                    {del.errorMessages.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            {hasChanges && previewState.status === "success" && (
              <p className="text-right text-sm text-muted-foreground">
                Applying these changes will update{" "}
                <span className="font-medium">{previewState.data.affectedCaseCount.toLocaleString()}</span> cases within
                30 minutes.
              </p>
            )}
            <div className="flex items-center gap-2">
              {!isDisabled && hasChanges && <span className="text-xs text-muted-foreground">Unsaved changes</span>}

              {canDelete && (
                <Popover>
                  <PopoverTrigger
                    disabled={isRemoveDisabled}
                    className={buttonVariants({
                      variant: "outline",
                      className:
                        "gap-2 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive",
                    })}
                    aria-label={
                      configExists ? "Remove retention configuration" : "No retention configuration to remove"
                    }
                  >
                    {del.isSubmitting ? (
                      <LoaderCircleIcon className="size-4 animate-spin" />
                    ) : (
                      <XCircleIcon className="size-4" />
                    )}
                    Remove configuration
                  </PopoverTrigger>
                  <PopoverContent side="top" className="w-80">
                    <div className="space-y-3">
                      <p className="text-sm font-medium">Remove retention configuration?</p>
                      <p className="text-sm text-muted-foreground">
                        This clears the retention policy for{" "}
                        <span className="font-medium">
                          {loadedOrganisation}/{loadedProject}
                        </span>
                        . The brand will have no retention configuration until one is set again.
                      </p>
                      <div className="flex items-center justify-end gap-3">
                        <PopoverClose className={buttonVariants({ variant: "ghost", size: "sm" })}>Cancel</PopoverClose>
                        <PopoverClose
                          className={buttonVariants({ variant: "destructive", size: "sm" })}
                          onClick={() => del.formRef.current?.requestSubmit()}
                        >
                          Remove
                        </PopoverClose>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}

              {canWrite && (
                <Button
                  type="submit"
                  size="lg"
                  disabled={isDisabled || !hasChanges}
                  aria-label="Save retention configuration"
                >
                  {save.isSubmitting ? (
                    <>
                      <LoaderCircleIcon className="size-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <SaveIcon className="size-4" />
                      Save changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </section>
      </save.fetcher.Form>

      <ConfirmationDialog
        confirmationData={save.confirmationData}
        timeLeft={save.timeLeft}
        isSaving={save.isSubmitting}
        onConfirm={save.confirmAndSubmit}
        onCancel={save.reset}
        description="This will update the brand's retention duration and related settings. Review the details below before confirming."
      />

      <del.fetcher.Form ref={del.formRef} method="DELETE" className="hidden">
        <input type="hidden" name="intent" value="DELETE" />
        <input type="hidden" name="organisation" value={loadedOrganisation} />
        <input type="hidden" name="project" value={loadedProject} />
        {del.confirmationData != null && <input type="hidden" name="token" value={del.confirmationData.token} />}
      </del.fetcher.Form>

      <ConfirmationDialog
        confirmationData={del.confirmationData}
        timeLeft={del.timeLeft}
        isSaving={del.isSubmitting}
        onConfirm={del.confirmAndSubmit}
        onCancel={del.reset}
        title="Confirm retention removal"
        description="This will permanently remove the brand's retention configuration. Review the details below before confirming."
        confirmLabel="Confirm & remove"
        confirmingLabel="Removing…"
      />
    </>
  )
}

function useFetchPreview(
  organisation: string,
  project: string,
  duration: string,
  originalDuration: string,
): PreviewState {
  const [previewState, setPreviewState] = useState<PreviewState>({ status: "idle" })

  const fetchPreview = useMemo(
    () =>
      debounce((signal: AbortSignal, duration: string) => {
        if (duration === "" || duration === originalDuration) {
          setPreviewState({ status: "idle" })
          return
        }

        setPreviewState({ status: "loading" })

        void fetchAPI<AffectedCountResponse>(`/api/UnityViewer/Retention/${organisation}/${project}/affected-count`, {
          method: "POST",
          body: { duration },
          signal,
        })
          .then(handleResponse)
          .then((data) => setPreviewState({ status: "success", data }))
          .catch(async (err) => {
            if (signal.aborted) return

            let messages: string[] = []

            if (err instanceof Response) {
              try {
                const errData = (await err.json()) as unknown
                if (Array.isArray(errData) && errData.length > 0) {
                  const collected = (errData as Array<{ propertyName?: string; errorMessage?: string }>)
                    .filter((e) => Boolean(e?.errorMessage))
                    .map((e) => e.errorMessage!)
                  if (collected.length > 0) messages = collected
                }
              } catch (parseErr) {
                console.error("[Retention] Failed to parse affected-count error response:", parseErr)
              }
            } else if (err instanceof Error) {
              messages = [err.message]
            }

            setPreviewState({
              status: "error",
              message: messages.length === 0 ? ["Failed to calculate affected cases."] : messages,
            })
          })
      }, 150),
    [organisation, project, originalDuration],
  )

  useEffect(() => {
    const controller = new AbortController()

    // ensure this is always called, so it cancels in-flight calls
    fetchPreview(controller.signal, duration)

    return () => controller.abort()
  }, [duration, fetchPreview])

  return previewState
}

Retention.loader = loader
Retention.action = action
Retention.Main = Main
Retention.routeId = routeId

Main.action = action
